import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { useAuth } from '../context/AuthContext';
import { useFeatureFlagsQuery } from '../hooks/react-query/useFeatureFlagsQuery';
import { Loader } from '../modules/Loader/main';
import { FeatureFlagsContext, featureFlagsValue } from './context';

/** How long the app waits for flag state before it renders with every flag off. */
const FLAGS_WAIT_MS = 3_000;

/**
 * Loads feature flag state for the signed-in user and holds it in context.
 *
 * useFeatureFlag reads a flag once per mount, so the app's first render
 * waits until ListFlags succeeds, fails, or takes longer than three seconds,
 * whichever comes first; until then the usual loading screen shows. After
 * that it never blocks again: a refresh (every five minutes, or on window
 * focus) only changes what the next mount reads.
 */
export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const signedIn = !!user.id;
  const flagsQuery = useFeatureFlagsQuery({ enabled: signedIn });

  const [waitedLongEnough, setWaitedLongEnough] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setWaitedLongEnough(true), FLAGS_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);

  const value = useMemo(() => featureFlagsValue(flagsQuery.data?.results ?? []), [flagsQuery.data]);

  // Signed out there is nothing to read; signing in reloads the page.
  const settled = !signedIn || !flagsQuery.isPending || waitedLongEnough;

  return <FeatureFlagsContext value={value}>{settled ? children : <Loader />}</FeatureFlagsContext>;
}
